/**
 * Client-side S0 keepsake protocol types (PC-B rev 9, sections 10, 10b.2).
 * Persisted shapes live in IDB (db.ts v9 stores); every revision/sequence
 * is a canonical decimal bigint STRING, never a JS number.
 */

export interface LocalPrayerMutationFenceV1 {
  prayerId: string;
  ownerScope: string | null;
  localMutationSeq: string;
  acknowledgedMutationSeq: string;
  inFlightMutationSeq: string | null;
  payloadDigest: string | null;
  authEpochAtMutation: number;
}

export interface LocalKeepsakeValidityIntentV1 {
  mutationId: string;
  ownerScope: string;
  prayerId: string;
  mutationKind: 'set-private' | 'set-included' | 'delete' | 'restore';
  expectedPolicyRevision: string;
  expectedVisibility: 'included' | 'private';
  expectedDeletion: 'live' | 'deleted';
  mutationDigest: string;
  localSeq: string;
  state: 'local-only' | 'in-flight' | 'acknowledged' | 'rejected';
  receiptPolicyRevision: string | null;
  receiptOwnerRevision: string | null;
}

export interface LocalPrayerCorpusFenceV1 {
  fenceVersion: 1;
  ownerScope: string;
  authEpoch: number;
  snapshotId: string;
  serverCorpusCount: number;
  serverCorpusDigest: string;
  localCorpusCount: number;
  localCorpusDigest: string;
  mismatchCount: number;
  state: 'matched' | 'unresolved';
}

export interface KeepsakePolicyPinV1 {
  responseVersion: 1;
  ownerId: string;
  prayerId: string;
  prayerExists: boolean;
  policyRevision: string;
  visibility: 'included' | 'private';
  deletion: 'live' | 'deleted';
  ownerValidityRevision: string;
}

/** Stored pin row: keyed (ownerId, authEpoch, prayerId). */
export interface StoredPolicyPin {
  key: string; // `${ownerId}:${authEpoch}:${prayerId}`
  pin: KeepsakePolicyPinV1;
  storedAt: string;
}

export function policyPinKey(ownerId: string, authEpoch: number, prayerId: string): string {
  return `${ownerId}:${authEpoch}:${prayerId}`;
}

/** Arbitrary-precision +1 on a canonical decimal bigint string. */
export function decimalIncrement(v: string): string {
  if (!/^(0|[1-9][0-9]*)$/.test(v)) throw new Error('decimalIncrement: non-canonical');
  const digits = v.split('');
  let i = digits.length - 1;
  for (;;) {
    if (digits[i] !== '9') {
      digits[i] = String(Number(digits[i]) + 1);
      return digits.join('');
    }
    digits[i] = '0';
    if (i === 0) return '1' + digits.join('');
    i--;
  }
}

/** Arbitrary-precision comparison (never a JS number). */
export function decimalCompare(a: string, b: string): -1 | 0 | 1 {
  if (a.length !== b.length) return a.length < b.length ? -1 : 1;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}
